import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import { PageContainer } from "../../components/primitives/layout/PageContainer";
import { PageHeader } from "../../components/primitives/layout/PageHeader";
import { Panel } from "../../components/primitives/surfaces/Panel";
import { Section } from "../../components/primitives/layout/Section";
import { SectionLabel } from "../../components/primitives/data-display/SectionLabel";
import { Note } from "../../components/primitives/data-display/Note";
import { AvatarUpload } from "../../components/primitives/inputs/AvatarUpload";
import { LoadingState } from "../../components/primitives/feedback/LoadingState";
import { ExpertProfileFields } from "../../components/domain/inputs/ExpertProfileFields";
import { useTaxonomy } from "../../context/TaxonomyProvider";
import { LOCALE_KEYS, LOCALES, type Locale } from "@shared/types/locale.ts";
import { useAccount } from "./useAccount";

/** The account Profile section. Shows the expert profile when the user has that role. */
export function AccountProfile() {
  const { t } = useTranslation();
  const {
    email,
    isExpert,
    profile,
    profileLoading,
    fullName,
    setFullName,
    credentials,
    setCredentials,
    bio,
    setBio,
    saveAccount,
    canSave,
    saving,
    saveError,
    handleAvatarUpload,
    uploading,
    uploadError,
    locale,
    changeLocale,
  } = useAccount();
  const taxonomy = useTaxonomy();

  return (
    <PageContainer embedded>
      <PageHeader
        size="compact"
        title={t("account.sections.profile")}
        subtitle={t("account.profile.subtitle")}
      />

      <Panel>
        <Section title={t("account.yourAccount")} gap={2}>
          <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" gap={{ xs: 2, sm: 3 }}>
            {isExpert && !profileLoading && (
              <AvatarUpload
                src={profile?.avatarUrl ?? undefined}
                name={profile?.fullName}
                label={t("expertInsights.account.changePhoto")}
                onUpload={handleAvatarUpload}
              />
            )}
            <Stack gap={1.5} flex={1} width="100%">
              {isExpert && !profileLoading && (
                <TextField
                  label={t("expertInsights.account.fullName")}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={saving || uploading}
                />
              )}
              <TextField
                label={t("auth.email")}
                value={email ?? ""}
                size="small"
                fullWidth
                disabled
                slotProps={{ input: { readOnly: true } }}
              />
            </Stack>
          </Stack>
          {uploadError && <Alert severity="error">{uploadError}</Alert>}
        </Section>
      </Panel>

      <Panel>
        <Section title={t("account.language.title")} gap={1.5}>
          <TextField
            select
            label={t("account.language.label")}
            value={locale}
            onChange={(e) => changeLocale(e.target.value as Locale)}
            size="small"
            fullWidth
            helperText={t("account.language.help")}
          >
            {LOCALE_KEYS.map((key) => (
              <MenuItem key={key} value={key}>
                {LOCALES[key].flag} {LOCALES[key].label}
              </MenuItem>
            ))}
          </TextField>
        </Section>
      </Panel>

      {isExpert && (
        <Panel>
          <Section title={t("account.expertProfile")} gap={1.5}>
            {profileLoading ? (
              <LoadingState label={t("account.loadingProfile")} />
            ) : (
              <Stack gap={2.5}>
                <ExpertProfileFields
                  credentials={credentials}
                  onCredentialsChange={setCredentials}
                  bio={bio}
                  onBioChange={setBio}
                  disabled={saving || uploading}
                />
                <Stack gap={0.75}>
                  <SectionLabel>{t("expertInsights.account.authorizedFieldsLabel")}</SectionLabel>
                  {profile && profile.fields.length > 0 ? (
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {profile.fields.map((urn) => {
                        const name = taxonomy.findField(urn)?.enName;
                        return name ? <Chip key={urn} label={name} size="medium" /> : null;
                      })}
                    </Stack>
                  ) : (
                    <Note>{t("expertInsights.account.noAuthorizedFields")}</Note>
                  )}
                </Stack>
              </Stack>
            )}
          </Section>
        </Panel>
      )}

      {!profileLoading && (
        <Stack gap={1.5} alignItems="flex-end">
          {saveError && <Alert severity="error">{saveError}</Alert>}
          <Button variant="contained" onClick={() => void saveAccount()} disabled={!canSave}>
            {saving ? t("expertInsights.account.saving") : t("expertInsights.account.save")}
          </Button>
        </Stack>
      )}
    </PageContainer>
  );
}
